import { action, batch, computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { isEqual } from 'es-toolkit';
import type { IdeaPlanTypes, SelectedIdea } from 'src/components/menus/IdeasMenuGrouped/types';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import { UniversePlanDTO } from 'src/features/universe/dtos/UniversePlanDTO';
import { modelDisposableStack } from 'src/lib/signals/modelDisposableStack';
import type { IdeasEvents, IdeasSignals } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';
import { z } from 'zod';

/**
 * Legacy blobs persisted `ideas` as `(Idea | string)[] | null`. Only the ids
 * are meaningful for rehydration — full idea objects are re-fetched — so the
 * schema normalizes everything down to a string-id array.
 */
const ideaIdsSchema = z.codec(z.unknown().optional(), z.array(z.string()), {
    decode: (value) => {
        if (!Array.isArray(value)) return [];
        return value
            .map((entry: unknown) =>
                typeof entry === 'string' ? entry : (entry as { id?: unknown } | null | undefined)?.id,
            )
            .filter((id): id is string => typeof id === 'string');
    },
    encode: (ideaIds) => ideaIds,
});

export const ideaSelectionStateSchema = z.object({
    ideas: ideaIdsSchema,
    universeId: z.string().nullable().default(null),
});

export type IdeaSelectionState = z.output<typeof ideaSelectionStateSchema>;

export type IdeaSelectionRuntimeState =
    | { type: 'loading' }
    | { type: 'ready' }
    | { type: 'error'; stage: 'fast-ideas' | 'slow-idea'; error: Error };

/**
 * Context-specific idea resolution, injected by the widget impl. Workspace
 * and standalone instantiations share everything except
 * `resolveInitialUniverseId` (standalone pages seed the workspace default
 * universe); the differences are these injected resolvers, not forked
 * model code.
 */
export interface IdeaSelectionResolvers {
    /** Maps the persisted universe id to the one the widget should start with. */
    resolveInitialUniverseId(persisted: string | null): Promise<string | null>;
    /** Resolves fast idea ids to DTOs. Individual failures should be dropped, not thrown. */
    resolveFastIdeas(ideaIds: readonly string[]): Promise<IdeaDTO<IdeaPlanDTO>[]>;
    /** Resolves a universe/idea id to the slow idea DTO, or null when unset. */
    resolveSlowIdea(universeId: string | null): Promise<IdeaDTO<IdeaPlanDTO> | null>;
}

export type IdeaSelectionModelEventBus = EventBus<IdeasSignals<IdeaPlanTypes>, IdeasEvents>;

export interface IdeaSelectionModelInit {
    persisted: IdeaSelectionState;
    logger: Logger;
    resolvers: IdeaSelectionResolvers;
    /** Gates multi-fast-idea selection; when off, only the first idea reaches the table. */
    multiIdeaSelectEnabled: ReadonlySignal<boolean>;
}

export interface IdeaSelectionModelShape {
    readonly state: ReadonlySignal<IdeaSelectionRuntimeState>;
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: IdeaSelectionModelEventBus;
    /** Resolved fast idea DTOs, already clamped by the multi-select flag. */
    readonly fastIdeas: ReadonlySignal<IdeaDTO<IdeaPlanDTO>[]>;
    readonly slowIdea: ReadonlySignal<IdeaDTO<IdeaPlanDTO> | null>;
    /** Query cache keys — DTO identity is not stable across fetches. */
    readonly fastIdeasCacheKey: ReadonlySignal<string>;
    readonly slowIdeaCacheKey: ReadonlySignal<string>;
    toJSON(): IdeaSelectionState;
    markClean(): void;
}

/**
 * Resolves a slow-idea selection payload to the universe/idea id that should
 * be persisted onto the widget state.
 */
export function slowIdeaSelectionToUniverseId(ideas: readonly (IdeaDTO | { universeId: string })[]): string | null {
    const idea = ideas[0];
    if (!idea) return null;
    if ('universeId' in idea) return idea.universeId;
    return (idea as IdeaDTO<IdeaPlanDTO>).idea.id ?? null;
}

function asError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

/**
 * Owns the widget's idea selection: persisted fast idea ids and slow
 * universe/idea id, their resolution to DTOs, and the ideas-menu EventBus
 * contract. Initial resolution completes before `state` flips to `ready`,
 * so a table query gated on readiness is built from final idea inputs.
 */
export const IdeaSelectionModel: ModelConstructor<IdeaSelectionModelShape, [IdeaSelectionModelInit]> = createModel<
    IdeaSelectionModelShape,
    [IdeaSelectionModelInit]
>((init: IdeaSelectionModelInit) => {
    const persistedState: Signal<IdeaSelectionState> = signal({
        ideas: init.persisted.ideas,
        universeId: init.persisted.universeId,
    });
    const cleanState: Signal<IdeaSelectionState> = signal(persistedState.value);

    const fastIdeas: Signal<IdeaDTO<IdeaPlanDTO>[]> = signal([]);
    const slowIdea: Signal<IdeaDTO<IdeaPlanDTO> | null> = signal(null);
    const fastIdeasCacheKey = signal(v4());
    const slowIdeaCacheKey = signal(v4());
    const runtimeState = signal<IdeaSelectionRuntimeState>({ type: 'loading' });

    const disposableStack = modelDisposableStack();

    const setFastIdeasResolved = (ideas: IdeaDTO<IdeaPlanDTO>[]): void => {
        const current = fastIdeas.value;
        const changed = ideas.length !== current.length || ideas.some((idea, index) => idea !== current[index]);
        if (!changed) return;
        fastIdeas.value = ideas;
        fastIdeasCacheKey.value = v4();
    };

    const setSlowIdeaResolved = (idea: IdeaDTO<IdeaPlanDTO> | null): void => {
        if (slowIdea.value === idea) return;
        slowIdea.value = idea;
        slowIdeaCacheKey.value = v4();
    };

    /** Applies a fast-idea selection: DTOs for the table, ids for persistence. */
    const setFastIdeas = (ideas: IdeaDTO<IdeaPlanDTO>[]): void => {
        const ids = ideas.map((idea) => idea.idea.id).filter((id): id is string => typeof id === 'string');
        if (!isEqual(ids, persistedState.value.ideas)) {
            persistedState.value = { ...persistedState.value, ideas: ids };
        }
        setFastIdeasResolved(ideas);
    };

    /** Applies a slow-idea selection; `id` is the universe or idea id to persist. */
    const setSlowIdea = (idea: IdeaDTO<IdeaPlanDTO> | null, id: string | null): void => {
        if (persistedState.value.universeId !== id) {
            persistedState.value = { ...persistedState.value, universeId: id };
        }
        setSlowIdeaResolved(idea);
    };

    /**
     * Applies the initial resolution results and the ready transition as one
     * atomic state change. An action because the resolution continuation runs
     * outside createModel's implicit action wrapping; consumers gated on
     * `ready` must observe the resolved DTOs in the same transition.
     */
    const completeInitialization = action(
        (resolvedFastIdeas: IdeaDTO<IdeaPlanDTO>[], resolvedSlowIdea: IdeaDTO<IdeaPlanDTO> | null): void => {
            setFastIdeasResolved(resolvedFastIdeas);
            setSlowIdeaResolved(resolvedSlowIdea);
            runtimeState.value = { type: 'ready' };
        },
    );

    /**
     * Initial resolution. Fast and slow ideas resolve in parallel; the state
     * only flips to `ready` once both are final.
     */
    const initialize = async (): Promise<void> => {
        let initialUniverseId = init.persisted.universeId;
        try {
            initialUniverseId = await init.resolvers.resolveInitialUniverseId(init.persisted.universeId);
        } catch (error) {
            init.logger.warn('Idea selection failed to resolve the initial universe; continuing without one', {
                error,
            });
        }
        if (disposableStack.disposed) return;
        if (initialUniverseId !== persistedState.value.universeId) {
            // Seeded default (standalone) — part of initialization, not a user edit. Batched
            // (this await continuation runs outside createModel's implicit action wrapping) so
            // `dirty` (persisted vs clean) never transiently flips between the two writes.
            batch(() => {
                persistedState.value = { ...persistedState.value, universeId: initialUniverseId };
                cleanState.value = { ...cleanState.value, universeId: initialUniverseId };
            });
        }

        const [fastResult, slowResult] = await Promise.allSettled([
            init.resolvers.resolveFastIdeas(init.persisted.ideas),
            init.resolvers.resolveSlowIdea(initialUniverseId),
        ]);
        if (disposableStack.disposed) return;

        const fail = (stage: 'fast-ideas' | 'slow-idea', error: unknown): void => {
            const normalized = asError(error);
            init.logger.error('Idea selection failed to initialize', { stage, error: normalized });
            runtimeState.value = { type: 'error', stage, error: normalized };
        };

        if (fastResult.status === 'rejected') {
            fail('fast-ideas', fastResult.reason);
            return;
        }
        if (slowResult.status === 'rejected') {
            fail('slow-idea', slowResult.reason);
            return;
        }

        completeInitialization(fastResult.value, slowResult.value);
    };

    void initialize();

    // The multi-select flag clamps what reaches the table without rewriting
    // the persisted selection.
    const effectiveFastIdeas: ReadonlySignal<IdeaDTO<IdeaPlanDTO>[]> = computed(() =>
        init.multiIdeaSelectEnabled.value ? fastIdeas.value : fastIdeas.value.slice(0, 1),
    );
    const effectiveFastIdeasCacheKey: ReadonlySignal<string> = computed(
        () => `${fastIdeasCacheKey.value}:${init.multiIdeaSelectEnabled.value ? 'multi' : 'single'}`,
    );

    const selectedFastIdeas: ReadonlySignal<SelectedIdea<IdeaPlanTypes>[]> = computed(() =>
        fastIdeas.value.map((idea) => ({
            type: 'idea' as const,
            id: idea.idea.id as string,
            name: idea.name ?? undefined,
        })),
    );

    const selectedSlowIdeas: ReadonlySignal<SelectedIdea<IdeaPlanTypes>[]> = computed(() => {
        const dto = slowIdea.value;
        if (!dto) return [];
        const plan: unknown = dto.plan;
        const universePlanId =
            typeof plan === 'object' && plan !== null && 'universeId' in plan
                ? (plan as { universeId: string }).universeId
                : undefined;
        const id = universePlanId ?? (dto.idea.id as string | undefined);
        if (!id) return [];
        return [{ type: universePlanId !== undefined ? ('universe' as const) : ('idea' as const), id }];
    });

    const eventBus: IdeaSelectionModelEventBus = EventBusBuilder.create()
        .addSignal('widget::selectedFastIdeas', () => selectedFastIdeas)
        .addSignal('widget::selectedSlowIdeas', () => selectedSlowIdeas)
        .addSignal(
            'widget::slowIdea',
            (): ReadonlySignal<IdeaDTO<IdeaPlanDTO> | null | undefined> => computed(() => slowIdea.value),
        )
        .addEvent('widget::onIdeasSelect', ({ ideas }) => {
            setFastIdeas(ideas.filter((idea): idea is IdeaDTO<IdeaPlanDTO> => 'idea' in idea));
        })
        .addEvent('widget::selectSlowIdea', ({ ideas }) => {
            const first = ideas[0];
            const id = slowIdeaSelectionToUniverseId(ideas);
            if (!first) {
                setSlowIdea(null, null);
            } else if ('universeId' in first) {
                setSlowIdea(UniversePlanDTO.fromUniverseId(first.universeId).toTempIdea(), id);
            } else {
                setSlowIdea(first as IdeaDTO<IdeaPlanDTO>, id);
            }
        })
        .build();

    return {
        state: computed(() => runtimeState.value),
        dirty: computed(() => !isEqual(persistedState.value, cleanState.value)),
        eventBus,
        fastIdeas: effectiveFastIdeas,
        slowIdea: computed(() => slowIdea.value),
        fastIdeasCacheKey: effectiveFastIdeasCacheKey,
        slowIdeaCacheKey: computed(() => slowIdeaCacheKey.value),
        toJSON(): IdeaSelectionState {
            return { ideas: [...persistedState.value.ideas], universeId: persistedState.value.universeId };
        },
        markClean(): void {
            cleanState.value = persistedState.value;
        },
    };
});

export type IdeaSelectionModel = Model<IdeaSelectionModelShape>;
